import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment-timezone";
import { CUSTOM_CONSTANTS } from "../../../Constants";
import PassengerApi from "../../../Services/Passenger";

const PackageProofInspectionModal = ({
  openDialog,
  tripData,
  handleClose,
  timezone,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);
  const [freshTrip, setFreshTrip] = useState(tripData);
  const [brokenImages, setBrokenImages] = useState({});

  // Reset broken images map when opening dialog or switching trip
  useEffect(() => {
    setBrokenImages({});
  }, [openDialog, tripData?._id]);

  const handleImageError = (field) => {
    setBrokenImages((prev) => ({ ...prev, [field]: true }));
  };

  // Fetch full live document from database when modal opens
  useEffect(() => {
    setFreshTrip(tripData);
    if (openDialog && tripData?._id) {
      PassengerApi.getPassengertriphistorybyid(tripData._id)
        .then((res) => {
          const tripObj = res?.data?.data?.data?.[0]?.userRideRequestData;
          if (tripObj) {
            setFreshTrip((prev) => ({
              ...prev,
              ...tripObj,
              driverdetails: tripObj.driverDetails || prev?.driverdetails,
              driverridedetails: tripObj.driverRide || prev?.driverridedetails,
            }));
          }
        })
        .catch((err) => {
          console.log("Error fetching live trip details:", err);
        });
    }
  }, [openDialog, tripData]);

  const currentTrip = freshTrip || tripData || {};

  // Validate image string
  const isValidImageUrl = (val) => {
    if (!val || typeof val !== "string") return false;
    const clean = val.trim();
    if (
      clean === "" ||
      clean === "null" ||
      clean === "undefined" ||
      clean === "{}" ||
      clean === "NaN" ||
      clean.startsWith("/assets/images/package_audit/")
    ) {
      return false;
    }
    return true;
  };

  // Resolve base image URLs safely (returns null if invalid)
  const getFullImageUrl = (imageSrc) => {
    if (!isValidImageUrl(imageSrc)) {
      return null;
    }
    const cleanSrc = imageSrc.trim();
    if (
      cleanSrc.startsWith("http://") ||
      cleanSrc.startsWith("https://") ||
      cleanSrc.startsWith("data:") ||
      cleanSrc.startsWith("blob:")
    ) {
      return cleanSrc;
    }
    const cleanPath = cleanSrc.startsWith("/") ? cleanSrc : `/${cleanSrc}`;
    const base = (CUSTOM_CONSTANTS.API_BASE_URL || "").replace(/\/+$/, "");
    return `${base}${cleanPath}`;
  };

  const getImageSource = (trip, primaryField, altFields = []) => {
    if (!trip) return "";
    if (isValidImageUrl(trip[primaryField])) return trip[primaryField];
    for (const alt of altFields) {
      if (isValidImageUrl(trip[alt])) return trip[alt];
    }
    const camel = primaryField.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    if (isValidImageUrl(trip[camel])) return trip[camel];
    return "";
  };

  const isImageAvailable = (rawSrc, fieldKey) => {
    if (!isValidImageUrl(rawSrc)) return false;
    if (brokenImages[fieldKey]) return false;
    return true;
  };

  const tz = timezone || "UTC";

  const formatDateRange = () => {
    const startRaw =
      currentTrip.startedAt ||
      currentTrip.scheduleAt ||
      currentTrip.requestedAt ||
      currentTrip.tripBookedAt ||
      currentTrip.createdAt;
    const endRaw = currentTrip.completedAt || currentTrip.reachedAt;

    if (!startRaw && !endRaw) {
      return "18 May 2025, 07:21 PM - 07:26 PM";
    }

    const startDate = moment(startRaw?.$date || startRaw).tz(tz);
    const isValidStart = startDate.isValid();

    if (endRaw) {
      const endDate = moment(endRaw?.$date || endRaw).tz(tz);
      const isValidEnd = endDate.isValid();

      if (isValidStart && isValidEnd) {
        if (startDate.isSame(endDate, "day")) {
          return `${startDate.format("DD MMM YYYY, hh:mm A")} - ${endDate.format("hh:mm A")}`;
        }
        return `${startDate.format("DD MMM YYYY, hh:mm A")} - ${endDate.format("DD MMM YYYY, hh:mm A")}`;
      }
    }

    if (isValidStart) {
      return startDate.format("DD MMM YYYY, hh:mm A");
    }

    return "18 May 2025, 07:21 PM - 07:26 PM";
  };

  const getPrePickupTime = () => {
    const d =
      currentTrip.requestedAt || currentTrip.tripBookedAt || currentTrip.createdAt;
    if (d && moment(d?.$date || d).isValid()) {
      return moment(d?.$date || d).tz(tz).format("hh:mm A");
    }
    return "--";
  };

  const getPickupTime = () => {
    const d = currentTrip.startedAt || currentTrip.scheduleAt || currentTrip.requestedAt;
    if (d && moment(d?.$date || d).isValid()) {
      return moment(d?.$date || d).tz(tz).format("hh:mm A");
    }
    return "--";
  };

  const getDeliveryTime = () => {
    const d = currentTrip.completedAt;
    if (d && moment(d?.$date || d).isValid()) {
      return moment(d?.$date || d).tz(tz).format("hh:mm A");
    }
    return "--";
  };

  const driverName = currentTrip.driverdetails
    ? `${currentTrip.driverdetails.firstName || ""} ${currentTrip.driverdetails.lastName || ""}`.trim()
    : currentTrip.driver_name || "Raj";

  const vehicleNumber =
    currentTrip.driverridedetails?.vehicleNumber ||
    currentTrip.vehicle_no ||
    "";

  const senderName =
    currentTrip.sender_name ||
    (currentTrip.userdetails
      ? `${currentTrip.userdetails.firstName || ""} ${currentTrip.userdetails.lastName || ""}`.trim()
      : "") ||
    "Dhamodharan";

  const rawBookingId = currentTrip.bookingId || currentTrip.trip_id || "";
  const formattedTripId = rawBookingId
    ? rawBookingId.toString().startsWith("#")
      ? rawBookingId
      : `#${rawBookingId}`
    : "#--";

  // 1. Customer Uploaded Images (useruploadimage)
  const customerImages = useMemo(() => {
    const frontSrc = getImageSource(currentTrip, "parcel_front_image");
    const backSrc = getImageSource(currentTrip, "parcel_back_image");
    const sideSrc = getImageSource(currentTrip, "parcel_side_image");

    return [
      {
        field: "parcel_front_image",
        label: "IMG_01: Package Sealed",
        rawSrc: frontSrc,
        url: getFullImageUrl(frontSrc),
        hasImage: isImageAvailable(frontSrc, "parcel_front_image"),
      },
      {
        field: "parcel_back_image",
        label: "IMG_02: Fragile Tag",
        rawSrc: backSrc,
        url: getFullImageUrl(backSrc),
        hasImage: isImageAvailable(backSrc, "parcel_back_image"),
      },
      {
        field: "parcel_side_image",
        label: "IMG_03: Invoice Receipt",
        rawSrc: sideSrc,
        url: getFullImageUrl(sideSrc),
        hasImage: isImageAvailable(sideSrc, "parcel_side_image"),
      },
    ];
  }, [currentTrip, brokenImages]);

  // 2. Driver Pick-up Images (driveruploadimages - pickup)
  const driverPickupImages = useMemo(() => {
    const frontSrc = getImageSource(currentTrip, "pickup_parcel_front_image");
    const backSrc = getImageSource(currentTrip, "pickup_parcel_back_image");
    const sideSrc = getImageSource(currentTrip, "pickup_parcel_side_image");

    return [
      {
        field: "pickup_parcel_front_image",
        label: "IMG_01: Package Sealed",
        rawSrc: frontSrc,
        url: getFullImageUrl(frontSrc),
        hasImage: isImageAvailable(frontSrc, "pickup_parcel_front_image"),
      },
      {
        field: "pickup_parcel_back_image",
        label: "IMG_02: Fragile Tag",
        rawSrc: backSrc,
        url: getFullImageUrl(backSrc),
        hasImage: isImageAvailable(backSrc, "pickup_parcel_back_image"),
      },
      {
        field: "pickup_parcel_side_image",
        label: "IMG_03: Invoice Receipt",
        rawSrc: sideSrc,
        url: getFullImageUrl(sideSrc),
        hasImage: isImageAvailable(sideSrc, "pickup_parcel_side_image"),
      },
    ];
  }, [currentTrip, brokenImages]);

  // 3. Driver Delivery Proof Images (driveruploadimages - drop)
  const driverDropImages = useMemo(() => {
    const frontSrc = getImageSource(currentTrip, "drop_parcel_front_image");
    const backSrc = getImageSource(currentTrip, "drop_parcel_back_image");
    const sideSrc = getImageSource(currentTrip, "drop_parcel_side_image");

    return [
      {
        field: "drop_parcel_front_image",
        label: "IMG_01: Package Sealed",
        rawSrc: frontSrc,
        url: getFullImageUrl(frontSrc),
        hasImage: isImageAvailable(frontSrc, "drop_parcel_front_image"),
      },
      {
        field: "drop_parcel_back_image",
        label: "IMG_02: Fragile Tag",
        rawSrc: backSrc,
        url: getFullImageUrl(backSrc),
        hasImage: isImageAvailable(backSrc, "drop_parcel_back_image"),
      },
      {
        field: "drop_parcel_side_image",
        label: "IMG_03: Invoice Receipt",
        rawSrc: sideSrc,
        url: getFullImageUrl(sideSrc),
        hasImage: isImageAvailable(sideSrc, "drop_parcel_side_image"),
      },
    ];
  }, [currentTrip, brokenImages]);

  const customerUploadedCount = customerImages.filter((img) => img.hasImage).length;
  const pickupUploadedCount = driverPickupImages.filter((img) => img.hasImage).length;
  const dropUploadedCount = driverDropImages.filter((img) => img.hasImage).length;
  const totalVerifiedCount = customerUploadedCount + pickupUploadedCount + dropUploadedCount;

  // The 3 tabs matching the workflow stages
  const tabsConfig = [
    {
      id: "customer",
      tabLabel: "Customer Uploaded",
      badgeCount: customerUploadedCount,
      totalCount: customerImages.length,
      sectionNumber: "1",
      title: "Customer Uploaded Images",
      subtitle: `Submitted by ${senderName} during dispatch booking confirmation`,
      timeTag: `${getPrePickupTime()} • Pre-Pickup`,
      images: customerImages,
    },
    {
      id: "pickup",
      tabLabel: "Driver Pick-up",
      badgeCount: pickupUploadedCount,
      totalCount: driverPickupImages.length,
      sectionNumber: "2",
      title: "Driver Pick-up Images",
      subtitle: `Submitted by ${driverName} during dispatch booking confirmation`,
      timeTag: `${getPickupTime()} • Pick-up`,
      images: driverPickupImages,
    },
    {
      id: "delivery",
      tabLabel: "Driver Delivery Proof",
      badgeCount: dropUploadedCount,
      totalCount: driverDropImages.length,
      sectionNumber: "3",
      title: "Driver Delivery Proof Images",
      subtitle: `Submitted by ${driverName} during dispatch booking confirmation`,
      timeTag: `${getDeliveryTime()} • Delivered`,
      images: driverDropImages,
    },
  ];

  const currentSection = tabsConfig[activeTab] || tabsConfig[0];

  if (!openDialog || !tripData) return null;

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "920px",
            maxWidth: "94vw",
            borderRadius: "26px",
            padding: "28px 32px",
            backgroundColor: "#ffffff",
            boxShadow: "0 25px 70px -15px rgba(0, 0, 0, 0.25)",
            border: "none",
            overflow: "hidden",
            margin: "16px",
          },
        }}
      >
        <DialogContent sx={{ padding: 0, overflow: "visible" }}>
          {/* Header Bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {/* Left Header: Icon + Info */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "16px" }}>
              {/* Black Box / Parcel Icon matching screenshot */}
              <Box
                sx={{
                  width: "50px",
                  height: "50px",
                  borderRadius: "15px",
                  backgroundColor: "#000000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.18)",
                }}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="4" rx="1.5" />
                  <path d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
                  <rect x="9" y="12" width="6" height="3" rx="1" />
                </svg>
              </Box>

              {/* Title, Badge on line 2, and Subtitle on line 3 */}
              <Box>
                {/* Line 1: Title */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    fontSize: "20px",
                    color: "#0f172a",
                    letterSpacing: "-0.3px",
                    lineHeight: 1.25,
                    fontFamily: "inherit",
                  }}
                >
                  Trip {formattedTripId} Proof & Inspection Audit
                </Typography>

                {/* Line 2: Photos Verified Badge */}
                <Box sx={{ marginTop: "5px", marginBottom: "5px" }}>
                  {totalVerifiedCount > 0 ? (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        backgroundColor: "#ecfdf5",
                        border: "1px solid #bbf7d0",
                        color: "#10b981",
                        padding: "2px 10px",
                        borderRadius: "9999px",
                        fontSize: "11.5px",
                        fontWeight: 700,
                        letterSpacing: "0.2px",
                      }}
                    >
                      <span style={{ fontSize: "8px" }}>●</span>
                      <span>{totalVerifiedCount} of 9 Photos Verified</span>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        backgroundColor: "#fef2f2",
                        border: "1px solid #fecaca",
                        color: "#dc2626",
                        padding: "2px 10px",
                        borderRadius: "9999px",
                        fontSize: "11.5px",
                        fontWeight: 700,
                        letterSpacing: "0.2px",
                      }}
                    >
                      <span style={{ fontSize: "8px" }}>●</span>
                      <span>No Photos Uploaded</span>
                    </Box>
                  )}
                </Box>

                {/* Line 3: Driver, Sender, Time info */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                    fontSize: "12.5px",
                    color: "#64748b",
                  }}
                >
                  <span>
                    Driver:{" "}
                    <strong style={{ color: "#0f172a", fontWeight: 700 }}>
                      {driverName} {vehicleNumber ? `(${vehicleNumber})` : ""}
                    </strong>
                  </span>
                  <span>•</span>
                  <span>
                    Sender:{" "}
                    <strong style={{ color: "#0f172a", fontWeight: 700 }}>
                      {senderName}
                    </strong>
                  </span>
                  <span>•</span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {formatDateRange()}
                  </span>
                </Box>
              </Box>
            </Box>

            {/* Close Button matching screenshot */}
            <IconButton
              onClick={handleClose}
              sx={{
                width: "38px",
                height: "38px",
                borderRadius: "12px",
                backgroundColor: "#eef2ff",
                color: "#3730a3",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#e0e7ff",
                  transform: "scale(1.05)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: "19px" }} />
            </IconButton>
          </Box>

          {/* Navigation Tabs (3 Pills matching screenshot) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginTop: "20px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            {tabsConfig.map((tab, idx) => {
              const isActive = activeTab === idx;
              return (
                <Box
                  key={tab.id}
                  onClick={() => setActiveTab(idx)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "7px 18px",
                    borderRadius: "9999px",
                    cursor: "pointer",
                    fontSize: "12.5px",
                    fontWeight: 600,
                    transition: "all 0.2s ease",
                    backgroundColor: isActive ? "#000000" : "#f8fafc",
                    color: isActive ? "#ffffff" : "#475569",
                    border: isActive ? "1px solid #000000" : "1px solid #e2e8f0",
                    "&:hover": {
                      backgroundColor: isActive ? "#18181b" : "#f1f5f9",
                    },
                  }}
                >
                  <span>{tab.tabLabel}</span>
                  <Box
                    sx={{
                      backgroundColor: isActive ? "#334155" : "#e2e8f0",
                      color: isActive ? "#ffffff" : "#475569",
                      borderRadius: "9999px",
                      padding: "1px 7px",
                      fontSize: "11px",
                      fontWeight: 700,
                    }}
                  >
                    {tab.badgeCount}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Content Card / Section below Tabs */}
          <Box
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "18px",
              padding: "20px 22px",
              backgroundColor: "#ffffff",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
            }}
          >
            {/* Section Header Row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              {/* Left Side: Number Badge + Titles */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box
                  sx={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    backgroundColor: "#e0e7ff",
                    color: "#4f46e5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "14px",
                    flexShrink: 0,
                  }}
                >
                  {currentSection.sectionNumber}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "15.5px",
                      fontWeight: 800,
                      color: "#0f172a",
                      lineHeight: 1.2,
                    }}
                  >
                    {currentSection.title} ({currentSection.badgeCount}/{currentSection.totalCount})
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#64748b",
                      marginTop: "3px",
                    }}
                  >
                    {currentSection.subtitle}
                  </Typography>
                </Box>
              </Box>

              {/* Right Side: Status/Time Pill Badge */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: "#eff6ff",
                  color: "#2563eb",
                  border: "1px solid #dbeafe",
                  borderRadius: "9999px",
                  padding: "4px 14px",
                  fontSize: "11.5px",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span>{currentSection.timeTag}</span>
              </Box>
            </Box>

            {/* 3 Images Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: "16px",
                marginTop: "18px",
              }}
            >
              {currentSection.images.map((item, idx) => {
                const isAvailable = Boolean(item.hasImage && item.url);

                return (
                  <Box
                    key={`${item.field}-${idx}`}
                    onClick={() => {
                      if (isAvailable) {
                        setPreviewImage(item);
                      }
                    }}
                    sx={{
                      position: "relative",
                      borderRadius: "14px",
                      overflow: "hidden",
                      height: "190px",
                      backgroundColor: isAvailable ? "#f8fafc" : "#fafafa",
                      border: isAvailable ? "1px solid #e2e8f0" : "1.5px dashed #cbd5e1",
                      cursor: isAvailable ? "pointer" : "default",
                      transition: "all 0.25s ease",
                      boxShadow: isAvailable ? "0 2px 5px rgba(0, 0, 0, 0.04)" : "none",
                      "&:hover": isAvailable
                        ? {
                            transform: "translateY(-3px)",
                            boxShadow: "0 10px 20px -3px rgba(0, 0, 0, 0.12)",
                            "& .zoom-overlay": {
                              opacity: 1,
                            },
                            "& img": {
                              transform: "scale(1.03)",
                            },
                          }
                        : {},
                    }}
                  >
                    {/* Overlay Tag in Top Left */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "10px",
                        left: "10px",
                        zIndex: 2,
                        backgroundColor: isAvailable
                          ? "rgba(15, 23, 42, 0.72)"
                          : "rgba(15, 23, 42, 0.65)",
                        backdropFilter: "blur(6px)",
                        color: "#ffffff",
                        fontSize: "10.5px",
                        fontWeight: 700,
                        padding: "4px 10px",
                        borderRadius: "6px",
                        letterSpacing: "0.2px",
                        pointerEvents: "none",
                      }}
                    >
                      {item.label}
                    </Box>

                    {/* Top Right Status Badge */}
                    {isAvailable ? (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "10px",
                          right: "10px",
                          zIndex: 2,
                          backgroundColor: "rgba(16, 185, 129, 0.9)",
                          backdropFilter: "blur(4px)",
                          color: "#ffffff",
                          fontSize: "10px",
                          fontWeight: 700,
                          padding: "3px 8px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          pointerEvents: "none",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Verified
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "10px",
                          right: "10px",
                          zIndex: 2,
                          backgroundColor: "#fee2e2",
                          border: "1px solid #fecaca",
                          color: "#dc2626",
                          fontSize: "10px",
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          pointerEvents: "none",
                        }}
                      >
                        <span style={{ fontSize: "6px" }}>●</span>
                        Not Uploaded
                      </Box>
                    )}

                    {/* Render Image or No Image Found State */}
                    {isAvailable ? (
                      <>
                        <img
                          src={item.url}
                          alt={item.label}
                          onError={() => handleImageError(item.field)}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                            transition: "transform 0.35s ease",
                          }}
                        />

                        {/* Hover Overlay Hint */}
                        <Box
                          className="zoom-overlay"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            transition: "opacity 0.2s ease",
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: "#ffffff",
                              color: "#0f172a",
                              borderRadius: "9999px",
                              padding: "6px 14px",
                              fontSize: "11px",
                              fontWeight: 700,
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
                            }}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <circle cx="11" cy="11" r="8" />
                              <line x1="21" y1="21" x2="16.65" y2="16.65" />
                              <line x1="11" y1="8" x2="11" y2="14" />
                              <line x1="8" y1="11" x2="14" y2="11" />
                            </svg>
                            Click to Expand
                          </Box>
                        </Box>
                      </>
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "20px 16px",
                          textAlign: "center",
                          userSelect: "none",
                        }}
                      >
                        {/* Muted photo placeholder icon */}
                        <Box
                          sx={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "14px",
                            backgroundColor: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#94a3b8",
                            marginBottom: "10px",
                          }}
                        >
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="2" y1="2" x2="22" y2="22" />
                            <path d="M10.41 10.41a2 2 0 1 1-2.83-2.83" />
                            <line x1="13.5" y1="13.5" x2="6" y2="21" />
                            <line x1="18" y1="18" x2="21" y2="21" />
                            <path d="M21 15V6a2 2 0 0 0-2-2H8.5" />
                            <path d="M3 8.5V19a2 2 0 0 0 2 2h10.5" />
                          </svg>
                        </Box>

                        <Typography
                          sx={{
                            fontSize: "13.5px",
                            fontWeight: 700,
                            color: "#475569",
                            marginBottom: "3px",
                            lineHeight: 1.2,
                          }}
                        >
                          No image found
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 500,
                            color: "#94a3b8",
                            lineHeight: 1.2,
                          }}
                        >
                          No photo uploaded for this stage
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Enlarged Photo Lightbox Modal */}
      {previewImage && previewImage.url && (
        <Dialog
          open={Boolean(previewImage)}
          onClose={() => setPreviewImage(null)}
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: "18px",
              overflow: "hidden",
              backgroundColor: "#0f172a",
              color: "#ffffff",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 20px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "14px" }}>
                {previewImage.label}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#94a3b8" }}>
                Trip {formattedTripId}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setPreviewImage(null)}
              sx={{
                color: "#ffffff",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
              }}
            >
              <CloseIcon sx={{ fontSize: "18px" }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              padding: "16px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxHeight: "80vh",
              overflow: "auto",
            }}
          >
            <img
              src={previewImage.url}
              alt={previewImage.label}
              onError={() => {
                handleImageError(previewImage.field);
                setPreviewImage(null);
              }}
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                borderRadius: "10px",
              }}
            />
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default PackageProofInspectionModal;
